<nav
    id="modern-top-navigation"
    #topNavigationRoot
    class="top-navigation"
    [attr.aria-label]="'navigation.topNavigation.aria.globalNavigation' | transloco"
>
    <p-dialog
        [visible]="logoutDialogVisible()"
        (visibleChange)="logoutDialogVisible.set($event)"
        [header]="'navigation.topNavigation.logoutDialog.header' | transloco"
        [modal]="true"
        [draggable]="false"
        [closable]="true"
        [closeOnEscape]="true"
        styleClass="tw-w-[90vw] tw-max-w-[600px]"
        data-test="top-navigation-logout-dialog"
    >
        <p>{{ 'navigation.topNavigation.logoutDialog.message' | transloco }}</p>

        <ng-template #footer>
            <div class="tw-flex tw-justify-end tw-gap-3">
                <p-button
                    [label]="'navigation.topNavigation.logoutDialog.cancel' | transloco"
                    variant="text"
                    (onClick)="onLogoutDialogReject()"
                    data-test="top-navigation-logout-dialog-cancel"
                />
                <p-button
                    [label]="'navigation.topNavigation.logoutDialog.confirm' | transloco"
                    (onClick)="onLogoutDialogAccept()"
                    data-test="top-navigation-logout-dialog-confirm"
                />
            </div>
        </ng-template>
    </p-dialog>

    <div class="top-navigation__mobile-header">
        <a
            uiSref="base.dashboard"
            class="top-navigation__mobile-brand"
            aria-label="Acquia Web Governance"
        >
            <span class="top-navigation__logo">
                <img src="/images/mobile-top-navigation-logo.png" alt="Acquia Web Governance" />
            </span>
            <span class="top-navigation__mobile-title">Web Governance</span>
        </a>

        <button
            type="button"
            class="top-navigation__mobile-menu-button"
            [attr.aria-label]="'navigation.topNavigation.mobileMenu.openButton' | transloco"
            aria-haspopup="menu"
            aria-controls="top-navigation-mobile-menu"
            [attr.aria-expanded]="isMobileMenuOpen()"
            (click)="toggleMobileMenu()"
        >
            <i class="pi pi-bars" aria-hidden="true"></i>
        </button>
    </div>

    @if (isMobileMenuOpen()) {
        <div
            id="top-navigation-mobile-menu"
            class="top-navigation__mobile-menu"
            role="menu"
            [attr.aria-label]="'navigation.topNavigation.mobileMenu.ariaLabel' | transloco"
        >
            <ul
                class="top-navigation__mobile-links"
                role="list"
                [attr.aria-label]="'navigation.topNavigation.mobileLinks.ariaLabel' | transloco"
            >
                @for (link of visibleTopLinks(); track link.sref) {
                    <li
                        class="top-navigation__mobile-link-item"
                        [class.top-navigation__mobile-link-item--active]="link.isActive()"
                    >
                        <a
                            [uiSref]="link.sref"
                            [attr.aria-current]="link.isActive() ? 'page' : null"
                        >
                            {{ link.label }}
                        </a>
                    </li>
                }
            </ul>

            <ul
                class="top-navigation__mobile-utilities"
                role="list"
                [attr.aria-label]="'navigation.topNavigation.mobileUtilities.ariaLabel' | transloco"
            >
                @for (utilityItem of visibleMobileUtilityItems(); track utilityItem.id) {
                    <li class="top-navigation__mobile-utility-item">
                        <button
                            type="button"
                            class="top-navigation__mobile-utility-button"
                            [attr.aria-label]="utilityItem.ariaLabel"
                            (click)="onMobileUtilityClick(utilityItem.id)"
                        >
                            <i [class]="utilityItem.iconClass" aria-hidden="true"></i>
                            <span>{{ utilityItem.label }}</span>
                        </button>
                    </li>
                }
            </ul>
        </div>
    }

    <div class="top-navigation__left">
        <a
            uiSref="base.dashboard"
            class="top-navigation__logo top-navigation__brand-button"
            aria-label="Acquia Web Governance"
        >
            <img src="/images/logo.svg" alt="Acquia Web Governance" />
        </a>

        <ul
            class="top-navigation__links"
            role="list"
            [attr.aria-label]="'navigation.topNavigation.desktopLinks.ariaLabel' | transloco"
        >
            @for (link of visibleTopLinks(); track link.sref) {
                <li
                    class="top-navigation__link-item"
                    [class.top-navigation__link-item--active]="link.isActive()"
                >
                    <a [uiSref]="link.sref" [attr.aria-current]="link.isActive() ? 'page' : null">
                        {{ link.label }}
                    </a>
                </li>
            }
        </ul>
    </div>

    <ul
        class="top-navigation__utilities"
        [attr.aria-label]="'navigation.topNavigation.desktopUtilities.ariaLabel' | transloco"
    >
        @for (utilityItem of visibleUtilityItems(); track utilityItem.id) {
            <li
                class="top-navigation__utility-item"
                [class.top-navigation__utility-item--active]="activeUtilityId() === utilityItem.id"
            >
                <button
                    type="button"
                    [attr.aria-label]="utilityItem.ariaLabel"
                    [pTooltip]="utilityItem.ariaLabel"
                    tooltipStyleClass="top-navigation__utility-tooltip"
                    tooltipPosition="bottom"
                    [attr.aria-pressed]="activeUtilityId() === utilityItem.id"
                    (click)="onUtilityButtonClick(utilityItem.id, $event)"
                >
                    <i [class]="utilityItem.iconClass" aria-hidden="true"></i>
                </button>
                @if (utilityItem.id === 'notifications' && activeUtilityId() === 'notifications') {
                    <mon-notifications-panel
                        class="top-navigation__notifications-panel"
                        (closeNotificationPanel)="activeUtilityId.set(null)"
                    />
                } @else if (utilityItem.id === 'help') {
                    <p-menu
                        #helpMenu
                        [popup]="true"
                        [model]="helpMenuItems()"
                        appendTo="body"
                        styleClass="help-menu"
                        (onShow)="activeUtilityId.set('help')"
                        (onHide)="onUtilityOverlayHide('help')"
                    >
                        <ng-template #item let-item>
                            <a pRipple class="p-menu-item-link">
                                <span class="p-menu-item-label">{{ item.label }}</span>
                                @if (item.state?.opensInNewTab) {
                                    <!-- TODO: Replace inline SVG with reusable icons -->
                                    <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" class="material-symbol-svg" viewBox="0 -960 960 960" fill="currentColor"><path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h240q17 0 28.5 11.5T480-800q0 17-11.5 28.5T440-760H200v560h560v-240q0-17 11.5-28.5T800-480q17 0 28.5 11.5T840-440v240q0 33-23.5 56.5T760-120H200Zm560-584L416-360q-11 11-28 11t-28-11q-11-11-11-28t11-28l344-344H600q-17 0-28.5-11.5T560-800q0-17 11.5-28.5T600-840h200q17 0 28.5 11.5T840-800v200q0 17-11.5 28.5T800-560q-17 0-28.5-11.5T760-600v-104Z"/></svg>
                                }
                            </a>
                        </ng-template>
                    </p-menu>
                } @else if (utilityItem.id === 'account') {
                    <p-popover #profilePopover styleClass="profile-popover" (onShow)="activeUtilityId.set('account')" (onHide)="onUtilityOverlayHide('account')">
                        <mon-profile-foldout
                            [user]="currentUser()"
                            [hasCustomer]="hasCustomer()"
                            [hasMultipleAccounts]="hasMultipleAccounts()"
                            [hasDataExport]="hasDataExport()"
                            (editProfile)="onProfileEditProfile()"
                            (myExports)="onProfileMyExports()"
                            (switchAccount)="onProfileSwitchAccount()"
                            (logout)="onProfileLogout()"
                            (closePanel)="closeActivePanel()"
                        />
                    </p-popover>
                }
            </li>
        }
    </ul>
</nav>
